import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, Scissors, Globe2, ShieldCheck, X, Shirt, Briefcase, Ruler, Play, Instagram, Facebook } from "lucide-react";
import { HERO_VIDEOS, CATEGORY_TREE, LOOKBOOK_TILES, SEASONAL_TILES, TRENDING_WEAR_CATS, SCROLL_BANNERS, TESTIMONIALS, FAQS } from "@/lib/constants";
import { api } from "@/lib/api";
import { useTestimonials, useReels, useSeasonalTiles, useApiList } from "@/lib/useContentBlocks";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProductCard from "@/components/ProductCard";
import FabricCard from "@/components/FabricCard";

// ------- Hero video helpers -------
// Pull the 11-char video id out of any YouTube URL form (watch / embed / shorts / youtu.be)
function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

// Renders a YouTube embed (iframe) or a local/hosted .mp4 (<video>) depending on the source.
function HeroVideo({ src }) {
  const ytId = getYouTubeId(src);

  // --- YouTube source -> iframe (cover-cropped, looped, muted, no controls) ---
  if (ytId) {
    const params = new URLSearchParams({
      autoplay: "1",
      mute: "1",
      loop: "1",
      playlist: ytId, // REQUIRED for loop to actually work
      controls: "0",
      showinfo: "0",
      modestbranding: "1",
      rel: "0",
      iv_load_policy: "3",
      playsinline: "1",
    });
    return (
      <div className="relative w-full h-full overflow-hidden">
        <iframe
          src={`https://www.youtube.com/embed/${ytId}?${params.toString()}`}
          title="hero-video"
          allow="autoplay; encrypted-media"
          frameBorder="0"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full pointer-events-none"
        />
      </div>
    );
  }

  // --- Local file ("/videos/hero.mp4") or any direct .mp4 URL -> <video> ---
  return (
    <video src={src} autoPlay loop muted playsInline className="w-full h-full object-cover" />
  );
}

// ------- 1. Hero (no text; local or YouTube videos) -------
function Hero({ heroVideo }) {
  // Admin-set hero video wins; otherwise fall back to the built-in set.
  const videos = heroVideo ? [heroVideo] : HERO_VIDEOS;
  // 1 video -> full-bleed (best for YouTube). 2 or 4 -> a video wall.
  const cols =
    videos.length >= 4
      ? "grid-cols-2 md:grid-cols-4"
      : videos.length === 2
      ? "grid-cols-2"
      : "grid-cols-1";

  return (
    <section className="relative h-[88vh] overflow-hidden bg-black" data-testid="hero-section">
  <div className={`absolute inset-0 grid ${cols}`}>
    {videos.map((v, i) => (
      <HeroVideo key={i} src={v} />
    ))}
  </div>
  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/80" />
  <div className="relative z-10 h-full flex flex-col justify-end pb-12 sq-container text-white">
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
      <div className="flex w-full flex-wrap gap-4">
        <Link
          to="/lookbook"
          data-testid="hero-lookbook-cta"
          className="group inline-flex items-center gap-2 font-display text-2xl md:text-3xl underline underline-offset-8 decoration-1 hover:decoration-2 transition"
        >
          Customise Your's
          <ArrowRight size={24} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </motion.div>
  </div>
</section>
  );
}

// ------- 2. Stats strip -------
function Stats() {
  const items = [
    { value: "4.9 / 5", star: true, label: "Customer Rating" },
    { value: "49+", label: "Cities Shipped" },
    { value: "8,000+", label: "Garments Tailored" },
    { value: "100%", label: "Fit Guarantee" },
  ];
  return (
    <section className="bg-white" data-testid="stats-strip">
      <div className="sq-container py-12 md:py-16 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
        {items.map((it, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="text-center"
          >
            <div className="font-display text-4xl md:text-5xl text-black flex items-baseline gap-1.5 justify-center">
              {it.value}
              {it.star && <Star size={26} className="fill-black text-black translate-y-[-2px]" />}
            </div>
            <div className="sq-eyebrow text-[#4B5563] mt-2">{it.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

// ------- 3a. Elevate Your Style (editorial split redesign) -------
function ElevateYourStyle() {
  const perks = ["Personal styling consultation", "In-atelier or video call", "No obligation to order"];
  return (
    <section className="bg-white" data-testid="banner-elevate">
      <div className="sq-container py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.6 }}
          className="relative aspect-[4/5] overflow-hidden bg-black order-2 md:order-1"
        >
          <img
            src="https://images.unsplash.com/photo-1507680434567-5739c80be1ac?crop=entropy&cs=srgb&fm=jpg"
            alt="Elevate Your Style"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="order-1 md:order-2"
        >
          <div className="sq-eyebrow text-squire-jeans">By Appointment</div>
          <h2 className="font-display text-5xl md:text-7xl mt-3 leading-[0.95]">Elevate Your Style</h2>
          <p className="mt-5 text-[#4B5563] max-w-md">Set an appointment with our master tailor — one-on-one, at our Hubli atelier.</p>
          <ul className="mt-8 space-y-3">
            {perks.map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-sm">
                <span className="w-1.5 h-1.5 bg-squire-jeans shrink-0" /> {f}
              </li>
            ))}
          </ul>
          <Link to="/contact" data-testid="banner-elevate-cta" className="sq-btn-primary mt-8 w-max">Book Now <ArrowRight size={16} /></Link>
        </motion.div>
      </div>
    </section>
  );
}

// ------- Corporate Gifting (single-line heading + inline white contact button) -------
function CorporateGifting() {
  return (
    <section className="bg-black text-white" data-testid="banner-corporate">
      <div className="sq-container py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div>
          <div className="sq-eyebrow text-white/60">For Teams & Occasions</div>
          <h2 className="font-display text-4xl md:text-6xl uppercase mt-3 leading-[0.95]">Corporate Gifting</h2>
          <p className="mt-5 text-white/70 max-w-md">Dress your whole team or gift with intent — bespoke shirts, blazers and curated hampers, tailored in bulk with the same hand-finished care. Custom branding and volume pricing available.</p>
          <Link
            to="/corporate-gifting"
            data-testid="banner-corporate-cta"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 mt-8 bg-white text-black text-xs uppercase tracking-[0.2em] font-semibold transition-colors duration-300 hover:bg-squire-jeans hover:text-white w-max"
          >
            Contact Us <ArrowRight size={15} />
          </Link>
        </div>
        <div className="relative aspect-[4/3] overflow-hidden">
          <img src="https://images.unsplash.com/photo-1520975916090-3105956dac38?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200" alt="Corporate gifting" className="w-full h-full object-cover" />
        </div>
      </div>
    </section>
  );
}

// ------- 3b. Our USP — Bespoke (category cards to dedicated product pages) -------
const BESPOKE_ICONS = { shirt: Shirt, suit: Briefcase, trousers: Ruler };

function BespokeUSP() {
  const [cards, setCards] = useState([]);

  useEffect(() => {
    api.get("/bespoke-categories").then(({ data }) => setCards(data || [])).catch(() => {});
  }, []);

  if (cards.length === 0) return null;

  return (
    <section className="sq-section bg-white" data-testid="bespoke-usp">
      <div className="sq-container">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="sq-eyebrow text-squire-jeans">Our USP</div>
            <h2 className="font-display text-4xl md:text-6xl mt-2">Bespoke, done properly.</h2>
            <p className="text-[#4B5563] mt-3 max-w-md">Pick a category, and step into a page built just for it — fabric, fit and finish, entirely on your terms.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {cards.map((c, i) => {
            const Icon = BESPOKE_ICONS[c.key] || Scissors;
            return (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <Link
                  to={`/bespoke/${c.key}`}
                  data-testid={`bespoke-usp-${c.key}`}
                  className="group relative block aspect-[3/4] overflow-hidden bg-black"
                >
                  <img src={c.image} alt={c.label} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/10" />
                  <div className="absolute top-5 left-5 w-9 h-9 flex items-center justify-center border border-white/40 group-hover:border-squire-jeanslight group-hover:bg-squire-jeans transition-colors">
                    <Icon size={16} className="text-white" />
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 text-white">
                    <div className="font-display text-2xl md:text-3xl">{c.label}</div>
                    <p className="text-xs text-white/70 mt-1">{c.tagline}</p>
                    <div className="mt-4 inline-flex items-center gap-2 sq-eyebrow text-[10px] group-hover:text-squire-jeanslight transition-colors">
                      Customise Now <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ------- 4. Category Frames with hover swap & scroll-reveal -------
// ------- Bestseller rails (BSC-style) -------
function BestsellerRail({ eyebrow, title, viewAll, items, render, testid }) {
  const railRef = useRef(null);
  const pausedRef = useRef(false);
  const scroll = (dir) => { const el = railRef.current; if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.85, 720), behavior: "smooth" }); };

  // Continuous left→right marquee. Items are duplicated for a seamless loop.
  useEffect(() => {
    const el = railRef.current;
    if (!el || items.length === 0) return;
    let raf;
    const step = () => {
      if (!pausedRef.current && el) {
        el.scrollLeft += 0.5;
        const half = el.scrollWidth / 2;
        if (el.scrollLeft >= half) el.scrollLeft -= half;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [items.length]);

  if (!items.length) return null;
  const loop = [...items, ...items];
  return (
    <section className="sq-section bg-white" data-testid={testid}>
      <div className="sq-container">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="sq-eyebrow text-squire-jeans">{eyebrow}</div>
            <h2 className="font-display text-3xl md:text-5xl mt-2">{title}</h2>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Link to={viewAll} className="hidden sm:inline text-xs uppercase tracking-[0.18em] border-b border-black hover:text-squire-jeans hover:border-squire-jeans pb-1">View all</Link>
            <button onClick={() => scroll(-1)} data-testid={`${testid}-prev`} className="w-10 h-10 rounded-full border border-black/20 hover:border-black grid place-items-center"><ArrowRight size={16} className="rotate-180" /></button>
            <button onClick={() => scroll(1)} data-testid={`${testid}-next`} className="w-10 h-10 rounded-full border border-black/20 hover:border-black grid place-items-center"><ArrowRight size={16} /></button>
          </div>
        </div>
      </div>
      <div
        ref={railRef}
        onMouseEnter={() => (pausedRef.current = true)}
        onMouseLeave={() => (pausedRef.current = false)}
        className="flex gap-5 overflow-x-auto sq-scroll-hidden px-6 md:px-[max(1.5rem,calc((100vw-1200px)/2))] pb-2"
      >
        {loop.map((it, i) => (
          <div key={`${it.id}-${i}`} className="shrink-0 w-[230px] md:w-[260px]">{render(it)}</div>
        ))}
      </div>
    </section>
  );
}

function BestsellerProducts() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { best_seller: true, limit: 10 } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
    // fallback: if no bestsellers flagged, show newest products
  }, []);
  const [fallback, setFallback] = useState([]);
  useEffect(() => { if (items.length === 0) api.get("/products", { params: { limit: 10 } }).then(({ data }) => setFallback(data.items || [])).catch(() => {}); }, [items.length]);
  const list = items.length ? items : fallback;
  return <BestsellerRail eyebrow="Most Loved" title="Bestselling Products" viewAll="/shop/shirts" items={list} testid="bestseller-products" render={(p) => <ProductCard product={p} />} />;
}

function BestsellerFabrics() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/fabrics", { params: { best_seller: true, limit: 10 } })
      .then(({ data }) => setItems((data.items || data) || []))
      .catch(() => setItems([]));
  }, []);
  const [fallback, setFallback] = useState([]);
  useEffect(() => { if (items.length === 0) api.get("/fabrics", { params: { limit: 10 } }).then(({ data }) => setFallback((data.items || data) || [])).catch(() => {}); }, [items.length]);
  const list = items.length ? items : fallback;
  return <BestsellerRail eyebrow="Handpicked Cloth" title="Bestselling Fabrics" viewAll="/fabrics" items={list} testid="bestseller-fabrics" render={(f) => <FabricCard fabric={f} />} />;
}

// ------- 3. Category frames (four houses) -------
function CategoryFrames() {
  const [openCat, setOpenCat] = useState(null);
  const [openSubs, setOpenSubs] = useState([]);
  const nav = useNavigate();
  const topCats = useApiList("/product-categories?top_level=true&home=true", []);

  const openCategory = async (c) => {
    setOpenCat(c);
    try {
      const { data } = await api.get("/product-categories", { params: { parent_key: c.key } });
      if (data && data.length > 0) { setOpenSubs(data); return; }
    } catch (e) { /* fall through to hardcoded fallback below */ }
    const fallback = CATEGORY_TREE.find((x) => x.key === c.key);
    setOpenSubs(fallback ? fallback.sub : []);
  };

  return (
    <section className="sq-section bg-white" data-testid="category-frames">
      <div className="sq-container">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="sq-eyebrow">The Atelier</div>
            <h2 className="font-display text-4xl md:text-6xl mt-2">Four houses, one wardrobe.</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {topCats.map((c, i) => (
            <CategoryTile key={c.key} c={c} index={i} onSelect={() => openCategory(c)} />
          ))}
        </div>
      </div>
      <AnimatePresence>
        {openCat && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setOpenCat(null)}
            data-testid="category-frames-popup"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
              className="bg-white max-w-4xl w-full p-10 max-h-[80vh] overflow-y-auto sq-scroll"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="sq-eyebrow text-squire-jeans">{openCat.label}</div>
                  <h3 className="font-display text-3xl mt-2">Choose a sub-category</h3>
                </div>
                <button data-testid="category-frames-popup-close" onClick={() => setOpenCat(null)}><X /></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {openSubs.map((s) => (
                  <button
                    key={s.key}
                    data-testid={`cat-popup-sub-${openCat.key}-${s.key}`}
                    onClick={() => { setOpenCat(null); nav(s.route || `/${openCat.key}`); }}
                    className="group aspect-[3/4] relative overflow-hidden bg-[#F5F5F5] border border-transparent hover:border-squire-jeans transition-colors"
                  >
                    <img src={s.image || openCat.image} alt={s.label} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" />
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-left">
                      <div className="text-white font-display text-lg">{s.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

const CATEGORY_BOOK_NOW_ROUTES = { fabric: "/fabrics", bespoke: "/bespoke", accessory: "/accessories" };

function CategoryTile({ c, index, onSelect }) {
  const [hover, setHover] = useState(false);
  return (
    <motion.div
      data-testid={`cat-frame-${c.key}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      className="text-left group"
    >
      <button
        type="button"
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        onClick={onSelect}
        className="block w-full text-left"
      >
        <div className="relative aspect-[3/4] overflow-hidden bg-[#F5F5F5]">
          <img src={c.image} alt={c.label} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${hover && c.hover ? "opacity-0" : "opacity-100"}`} />
          {c.hover && <img src={c.hover} alt={c.label} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${hover ? "opacity-100" : "opacity-0"}`} />}
        </div>
        <div className="mt-5 flex items-center justify-between">
          <div className="font-display text-2xl md:text-3xl">{c.label}</div>
          <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </button>
      <Link
        to={CATEGORY_BOOK_NOW_ROUTES[c.key] || `/shop/${c.key}`}
        data-testid={`cat-frame-${c.key}-book-now`}
        className="sq-btn-outline mt-4 w-full !py-2.5 !text-[10px] justify-center"
      >
        Book Now
      </Link>
    </motion.div>
  );
}

// ------- 5. Trending Lookbook bento -------
const LOOKBOOK_SPANS = ["md:col-span-5 md:row-span-2", "md:col-span-3", "md:col-span-4 md:row-span-2", "md:col-span-3", "md:col-span-5", "md:col-span-4", "md:col-span-3 md:row-span-2", "md:col-span-5"];

function TrendingLookbook() {
  const tiles = useApiList("/lookbook-tiles", []);
  if (!tiles.length) return null;

  const categoryTiles = tiles.filter((t) => (t.kind || "category") === "category");
  const accessoryTiles = tiles.filter((t) => t.kind === "accessory");
  // Left column = even-indexed category tiles, right column = odd-indexed.
  const leftCol = categoryTiles.filter((_, i) => i % 2 === 0);
  const rightCol = categoryTiles.filter((_, i) => i % 2 === 1);

  const linkFor = (t) => t.link || "/fabrics";
  const CatTile = ({ t, i }) => (
    <Link to={linkFor(t)} data-testid={`lookbook-cat-${t.id}`} className="relative block w-full overflow-hidden group bg-black aspect-[3/4]">
      <img src={t.image} alt={t.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
      <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
        <div className="font-display text-white text-2xl md:text-3xl leading-none">{t.label}</div>
        <ArrowRight size={18} className="text-white shrink-0 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
      </div>
    </Link>
  );
  const AccTile = ({ t }) => (
    <Link to={linkFor(t)} data-testid={`lookbook-acc-${t.id}`} className="relative block w-full overflow-hidden group bg-black aspect-[4/3]">
      <img src={t.image} alt={t.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
      {t.label && <div className="absolute bottom-3 left-3 right-3 text-white text-sm font-medium drop-shadow">{t.label}</div>}
    </Link>
  );

  return (
    <section className="sq-section bg-[#FDFDFD]" data-testid="trending-lookbook">
      <div className="sq-container">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
          <div>
            <div className="sq-eyebrow">Editorial</div>
            <h2 className="font-display text-4xl md:text-6xl mt-2">Trending Lookbook</h2>
            <p className="text-[#4B5563] mt-3 max-w-md">Shop the edit — tap a piece to explore the full collection.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {/* Left column — category tiles */}
          <div className="flex flex-col gap-4 md:gap-5">
            {leftCol.map((t, i) => <CatTile key={t.id} t={t} i={i} />)}
          </div>
          {/* Middle column — stacked accessory tiles */}
          <div className="flex flex-col gap-4 md:gap-5">
            {accessoryTiles.length ? accessoryTiles.map((t) => <AccTile key={t.id} t={t} />) : (
              <div className="border border-dashed border-black/15 rounded-lg aspect-[4/3] grid place-items-center text-xs text-[#9CA3AF]">Accessories</div>
            )}
          </div>
          {/* Right column — category tiles */}
          <div className="flex flex-col gap-4 md:gap-5">
            {rightCol.map((t, i) => <CatTile key={t.id} t={t} i={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

// ------- 6. Seasonal Spice -------
function SeasonalSpice() {
  const tiles = useSeasonalTiles(SEASONAL_TILES);
  return (
    <section className="sq-section bg-white" data-testid="seasonal-spice">
      <div className="sq-container">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="sq-eyebrow">Edits</div>
            <h2 className="font-display text-4xl md:text-6xl mt-2">Seasonal Spice</h2>
          </div>
          <Link to="/seasonal-spice" data-testid="seasonal-view-more" className="sq-btn-outline">View More</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {tiles.map((t, i) => <SeasonalCard key={t.id || i} t={t} i={i} />)}
        </div>
      </div>
    </section>
  );
}
function SeasonalCard({ t, i }) {
  const [hover, setHover] = useState(false);
  const to = t.slug ? `/shop-by-occasion/${t.slug}` : (t.route || "/seasonal-spice");
  return (
    <Link to={to} data-testid={`seasonal-tile-${i}`} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} className="group">
      <div className="relative aspect-[3/4] overflow-hidden">
        <img src={t.image} alt={t.name} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${hover ? "opacity-0" : "opacity-100"}`} />
        <img src={t.hover || t.image} alt={t.name} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${hover ? "opacity-100" : "opacity-0"}`} />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="font-display text-xl">{t.name}</div>
        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}

// ------- 7. Full-width horizontal banner (full-screen YouTube video) -------
function HorizontalBanner({ bannerVideo }) {
  return (
    <section className="relative h-screen overflow-hidden bg-black" data-testid="horizontal-banner">
      <div className="absolute inset-0">
        <HeroVideo src={bannerVideo || HERO_VIDEOS[0]} />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/30" />
      <div className="relative z-10 h-full sq-container flex items-end pb-16 text-white">
        <Link to="/bespoke" data-testid="horizontal-banner-cta" className="sq-btn-ghost-white w-max">Discover Bespoke <ArrowRight size={16} /></Link>
      </div>
    </section>
  );
}

// ------- 8. Trending Wear auto-rotator -------
function TrendingWear() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((v) => (v + 1) % TRENDING_WEAR_CATS.length), 5000);
    return () => clearInterval(t);
  }, [paused]);
  const cat = TRENDING_WEAR_CATS[i];
  return (
    <section className="bg-[#F5F5F5]" data-testid="trending-wear" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="sq-container py-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center min-h-[520px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={`img-${i}`}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.5 }}
            className="aspect-[4/5] overflow-hidden bg-black"
          >
            <img src={cat.image} alt={cat.label} className="w-full h-full object-cover" />
          </motion.div>
        </AnimatePresence>
        <div>
          <div className="sq-eyebrow text-squire-jeans">Trending Wear</div>
          <AnimatePresence mode="wait">
            <motion.h2
              key={`title-${i}`}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="font-display text-5xl md:text-7xl uppercase mt-3 leading-[0.95]"
            >
              {cat.label}
            </motion.h2>
          </AnimatePresence>
          <AnimatePresence mode="wait">
            <motion.p
              key={`why-${i}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="mt-6 text-[#4B5563] max-w-md"
            >
              {cat.why}
            </motion.p>
          </AnimatePresence>
          <Link to={`/category/${cat.key}`} data-testid="trending-wear-shop" className="sq-btn-primary mt-8">Shop Now <ArrowRight size={16} /></Link>
          <div className="mt-8 flex gap-2">
            {TRENDING_WEAR_CATS.map((_, k) => (
              <span key={k} className={`h-1 w-10 transition-colors ${k === i ? "bg-squire-jeans" : "bg-black/20"}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ------- 9. Scroll-triggered banner (3 images, parallax wipe) -------
function ScrollBanner() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [-60, 60]);
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const prevIdx = useRef(0);
  useEffect(() => {
    return scrollYProgress.on("change", (v) => {
      const next = Math.min(SCROLL_BANNERS.length - 1, Math.max(0, Math.floor(v * SCROLL_BANNERS.length)));
      if (next !== prevIdx.current) {
        setDir(next > prevIdx.current ? 1 : -1);
        prevIdx.current = next;
        setIdx(next);
      }
    });
  }, [scrollYProgress]);
  const b = SCROLL_BANNERS[idx];
  return (
    <section ref={ref} className="relative h-[640px] md:h-[820px] overflow-hidden bg-black" data-testid="scroll-banner">
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ clipPath: dir > 0 ? "inset(0 0 0 100%)" : "inset(0 100% 0 0)" }}
          animate={{ clipPath: "inset(0 0% 0 0%)" }}
          exit={{ clipPath: dir > 0 ? "inset(0 100% 0 0)" : "inset(0 0 0 100%)" }}
          transition={{ duration: 1.2, ease: [0.65, 0, 0.35, 1] }}
          className="absolute inset-0"
        >
          <motion.img style={{ y: parallaxY }} src={b.image} alt={b.name} className="absolute inset-0 w-full h-full object-cover scale-110" />
        </motion.div>
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/40" />

      <div className="relative z-10 h-full sq-container flex flex-col justify-between py-12 md:py-16 text-white">
        <div className="sq-eyebrow text-white/60">Now Trending · {String(idx + 1).padStart(2, "0")} / {String(SCROLL_BANNERS.length).padStart(2, "0")}</div>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <AnimatePresence mode="wait">
            <motion.h2
              key={`t-${idx}`}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.6 }}
              className="font-display text-5xl md:text-7xl max-w-xl leading-[0.95]"
            >
              {b.name}
            </motion.h2>
          </AnimatePresence>
          <div className="flex items-center gap-8">
            <div className="hidden md:flex items-center gap-3">
              {SCROLL_BANNERS.map((_, k) => (
                <span key={k} className={`h-1 transition-all duration-500 ${k === idx ? "w-10 bg-squire-jeanslight" : "w-4 bg-white/25"}`} />
              ))}
            </div>
            <Link to={b.route} data-testid="scroll-banner-shop" className="group relative inline-flex items-center gap-3 px-7 py-3.5 border border-white overflow-hidden shrink-0">
              <span className="absolute inset-0 bg-white -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-out" />
              <span className="relative sq-eyebrow group-hover:text-black transition-colors duration-300">Shop Now</span>
              <ArrowRight size={16} className="relative group-hover:translate-x-1 group-hover:text-black transition-all duration-300" />
            </Link>
          </div>
        </div>
      </div>

      {/* scroll-linked progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/10 z-10">
        <motion.div className="h-full bg-squire-jeanslight origin-left" style={{ scaleX: scrollYProgress }} />
      </div>
    </section>
  );
}

// ------- 10. Reels section (simplified, plays video, links to Instagram) -------
const DEFAULT_REELS = [
  { id: "r1", title: "Stitch Floor", image: "https://images.unsplash.com/photo-1591944489410-16ec1074a18e?crop=entropy&cs=srgb&fm=jpg", video_url: HERO_VIDEOS[0] },
  { id: "r2", title: "Cloth Story", image: "https://images.unsplash.com/photo-1633655442168-c6ef0ed2f984?crop=entropy&cs=srgb&fm=jpg", video_url: HERO_VIDEOS[0] },
  { id: "r3", title: "Wedding Day", image: "https://images.unsplash.com/photo-1617114919297-3c8ddb01f599?crop=entropy&cs=srgb&fm=jpg", video_url: HERO_VIDEOS[0] },
  { id: "r4", title: "Resort Edit", image: "https://images.pexels.com/photos/6766286/pexels-photo-6766286.jpeg", video_url: HERO_VIDEOS[0] },
  { id: "r5", title: "Black Tie", image: "https://images.unsplash.com/photo-1507680434567-5739c80be1ac?crop=entropy&cs=srgb&fm=jpg", video_url: HERO_VIDEOS[0] },
];

function Reels() {
  const reels = useReels(DEFAULT_REELS);
  const withVideo = reels.filter((r) => getYouTubeId(r.video_url)).slice(0, 4);
  const grid = withVideo.length ? withVideo : reels.slice(0, 4);
  return (
    <section className="sq-section bg-[#FDFDFD]" data-testid="reels-section">
      <div className="sq-container mb-10 text-center">
        <div className="sq-eyebrow text-squire-jeans">Atelier Reels</div>
        <h2 className="font-display text-4xl md:text-6xl mt-2">A peek behind the cloth</h2>
      </div>

      <div className="sq-container grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {grid.map((r, i) => {
          const id = getYouTubeId(r.video_url);
          return (
            <div key={r.id || i} data-testid={`reel-${r.id || i}`} className="relative aspect-[9/16] overflow-hidden rounded-xl bg-black">
              {id ? (
                <iframe
                  src={`https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&playsinline=1&rel=0`}
                  title={r.title || `Reel ${i + 1}`}
                  allow="autoplay; encrypted-media"
                  frameBorder="0"
                  className="absolute inset-0 w-full h-full pointer-events-none scale-[1.35]"
                />
              ) : (
                <img src={r.image} alt={r.title} className="absolute inset-0 w-full h-full object-cover" />
              )}
              {r.title && <div className="absolute bottom-3 left-3 right-3 font-display text-white text-lg drop-shadow">{r.title}</div>}
            </div>
          );
        })}
      </div>

      <div className="sq-container mt-10 flex items-center justify-center gap-4">
        <a href="https://www.instagram.com/squire_n_squire/" target="_blank" rel="noopener noreferrer" data-testid="reels-instagram-link"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white text-xs uppercase tracking-[0.15em] font-semibold hover:opacity-90 transition-opacity">
          <Instagram size={16} /> Instagram
        </a>
        <a href="https://www.facebook.com/squireandsquire" target="_blank" rel="noopener noreferrer" data-testid="reels-facebook-link"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1877f2] text-white text-xs uppercase tracking-[0.15em] font-semibold hover:opacity-90 transition-opacity">
          <Facebook size={16} /> Facebook
        </a>
      </div>
    </section>
  );
}

// ------- 11. Testimonials — 4 auto-rotating gradient cards -------
function TestimonialCard({ t }) {
  return (
    <div data-testid="testimonial-card" className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#3b4a9e] to-[#5b6fd6] p-[3px] shadow-lg">
      <div className="relative rounded-2xl bg-white px-6 pt-7 pb-16 h-full text-center overflow-hidden">
        <div className="flex justify-center gap-1 text-[#2b3a8c]">
          {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={16} className="fill-[#2b3a8c] text-[#2b3a8c]" />)}
        </div>
        <p className="italic text-[#374151] text-sm leading-relaxed mt-5 min-h-[110px]">{t.quote}</p>
        {/* yellow curve */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#f7d33f] to-[#f9e07a]" style={{ clipPath: "ellipse(120% 100% at 50% 100%)" }} />
        <div className="absolute bottom-4 left-0 right-0 px-4">
          <div className="font-display text-2xl text-[#1f2a63]">{t.name}</div>
          <div className="text-[11px] text-[#2b3a8c] uppercase tracking-[0.12em] mt-0.5">{t.role || t.city}</div>
        </div>
      </div>
    </div>
  );
}

function Testimonials() {
  const testimonials = useTestimonials(TESTIMONIALS);
  const [start, setStart] = useState(0);
  useEffect(() => {
    if (testimonials.length <= 4) return;
    const t = setInterval(() => setStart((s) => (s + 1) % testimonials.length), 3000);
    return () => clearInterval(t);
  }, [testimonials.length]);
  if (!testimonials.length) return null;
  // pick 4 visible, wrapping around
  const visible = Array.from({ length: Math.min(4, testimonials.length) }, (_, i) => testimonials[(start + i) % testimonials.length]);
  return (
    <section className="sq-section bg-[#f4f5fb]" data-testid="testimonials">
      <div className="sq-container">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="sq-eyebrow text-squire-jeans">Testimonials</div>
          <h2 className="font-display text-4xl md:text-5xl mt-2">What our clients say</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {visible.map((t, i) => (
            <div key={`${start}-${i}`} className="animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
              <TestimonialCard t={t} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ------- 12. FAQ -------
function FAQSection() {
  const faqs = useApiList("/faqs", []);
  const list = faqs.length ? faqs.map((f) => ({ q: f.question, a: f.answer })) : FAQS;
  return (
    <section className="sq-section bg-[#eaf1f1]" data-testid="faq-section">
      <div className="sq-container">
        <h2 className="font-display text-4xl md:text-6xl mb-10">Frequently Asked</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((f, i) => (
            <div key={i} data-testid={`faq-item-${i}`} className="group relative bg-white rounded-2xl p-6 min-h-[150px] shadow-sm hover:shadow-xl transition-all duration-300 cursor-default overflow-hidden">
              <div className="font-display text-lg md:text-xl leading-snug transition-transform duration-300 group-hover:-translate-y-1">{f.q}</div>
              <div className="text-[#4B5563] text-sm mt-3 max-h-0 opacity-0 group-hover:max-h-40 group-hover:opacity-100 transition-all duration-300 overflow-hidden">{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [videos, setVideos] = useState({ hero_video: "", banner_video: "" });
  useEffect(() => { api.get("/home-videos").then(({ data }) => setVideos(data || {})).catch(() => {}); }, []);
  return (
    <main data-testid="home-page">
      <Hero heroVideo={videos.hero_video} />
      <Stats />
      <BespokeUSP />
      <BestsellerProducts />
      <BestsellerFabrics />
      <TrendingLookbook />
      <SeasonalSpice />
      <HorizontalBanner bannerVideo={videos.banner_video} />
      <ElevateYourStyle />
      <TrendingWear />
      <CorporateGifting />
      <Reels />
      <Testimonials />
      <FAQSection />
    </main>
  );
}